import { type FragmentType, useFragment } from '@/gql/codegen';
import { graphql } from '@/gql/codegen/gql';
import { useAmplitudeLogEvent } from '@/routes/-hooks/useAmplitudeLogEvent';
import { Link } from '@tanstack/react-router';
import classNames from 'classnames';
import type React from 'react';
import styles from './index.module.scss';

import { LessonIcon } from './LessonIcon';
import { LessonStatusIcon } from './LessonStatusIcon';

export const COURSE_LESSON_FRAGMENT = graphql(/* GraphQL */ `
  fragment EmbaCourseLesson on CourseLesson {
    id
    slug
    name
    lessonContentType
    navigationStatus
    embaUserLessonProgress {
      id
      status
    }
  }
`);

export type LessonListProps = {
  embaUserAttendanceSlug: string;
  onClick: () => void;
  lesson: FragmentType<typeof COURSE_LESSON_FRAGMENT>;
  type?: 'sidebar' | 'lessons';
};

export const LessonList: React.FC<LessonListProps> = ({
  embaUserAttendanceSlug,
  onClick,
  lesson,
  type = 'sidebar',
}) => {
  const lessonData = useFragment(COURSE_LESSON_FRAGMENT, lesson);
  const trackAmplitudeLogEvent = useAmplitudeLogEvent();

  const isNextReccomendedLesson = lessonData.navigationStatus === 'recommended';
  const isCurrentLesson = lessonData.navigationStatus === 'current';
  const lessonStatus = lessonData.embaUserLessonProgress?.status || 'locked';
  const isNeedConfirm =
    !lessonData.embaUserLessonProgress &&
    !isNextReccomendedLesson &&
    !isCurrentLesson;

  const onLessonClick: React.MouseEventHandler = (event) => {
    if (!isNeedConfirm) {
      trackAmplitudeLogEvent('lessonOpenedFromList', {
        embaUserAttendanceSlug: embaUserAttendanceSlug,
        lessonSlug: lessonData.slug,
        isSkipped: false,
      });
      onClick();
      return;
    }

    if (
      // TODO : 本実装はmodal等の表示検討
      // eslint-disable-next-line
      confirm('受けるべきレッスンをスキップして他のレッスンに移動しますか？')
    ) {
      trackAmplitudeLogEvent('lessonOpenedFromList', {
        embaUserAttendanceSlug: embaUserAttendanceSlug,
        lessonSlug: lessonData.slug,
        isSkipped: true,
      });
      onClick();
      return;
    }

    event.preventDefault();
  };

  return (
    <li
      className={classNames(
        styles.lessonItem,
        styles[lessonData.navigationStatus],
        styles[lessonStatus],
        {
          [styles.sidebar]: type === 'sidebar',
          [styles.lessons]: type === 'lessons',
        },
      )}
    >
      {isCurrentLesson && <div className={styles.currentLessonMark} />}
      <Link
        to="/user-attendances/$userAttendanceSlug/lessons/$lessonSlug"
        params={{
          userAttendanceSlug: embaUserAttendanceSlug,
          lessonSlug: lessonData.slug,
        }}
        onClick={onLessonClick}
      >
        <span className={styles.lessonName}>
          <LessonIcon contentType={lessonData.lessonContentType} />
          <p>{lessonData.name}</p>
        </span>
        <span className={styles.statusIconArea}>
          <LessonStatusIcon
            status={lessonData.embaUserLessonProgress?.status}
            isNextReccomendedLesson={isNextReccomendedLesson}
          />
        </span>
      </Link>
    </li>
  );
};
